import { Component, Input, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { SwalToastService } from 'src/app/services/swal.service';
import { IAddAccommodation } from 'src/app/interfaces/Accommodation/IAddAccommodation';
import { AccommodationService } from 'src/app/services/accomodation.service';
import { AdmissionService } from 'src/app/services/admission.service';
import { BehaviorSubject, combineLatest, EMPTY, Subject, switchMap, takeUntil } from 'rxjs';
import { LocaleService } from 'src/app/services/locale.service';

@Component({
  selector: 'app-move-bed',
  templateUrl: './move-bed.component.html',
  styleUrls: ['./move-bed.component.css'],
  standalone: false,
})
export class MoveBedComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly visibility$ = new BehaviorSubject(false);
  @ViewChild('moveModalRef', { static: true }) moveModalRef!: NzModalRef;

  @Input() parent: any;

  @Input() isMoveVisible: boolean = false;
  @Input() selectedPatientFullName = '';
  @Input() selectedPatientID?: number;

  moveForm: FormGroup = new FormGroup({});

  moveBeds: { id: number; name: string }[] = [];

  isMoveLoading = false;
  moveSubmitted = false;

  constructor(
    private readonly fb: FormBuilder,
    private readonly admissionService: AdmissionService,
    private readonly accommodationService: AccommodationService,
    private readonly translate: TranslateService,
    private readonly swalToastService: SwalToastService,
    private readonly localeService: LocaleService,
  ) {
    this.moveForm = this.fb.group({
      accommodationValue: ['', Validators.required],
      newBedValue: [undefined, Validators.required],
      noteValue: [''],
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isMoveVisible']) {
      if (changes['isMoveVisible'].currentValue) {
        this.moveBeds = [];

        this.moveForm.reset();

        if (this.selectedPatientID) {
          this.moveForm
            .get('accommodationValue')
            ?.setValue(this.selectedPatientID);
          this.visibility$.next(true);
        }
      } else {
        this.visibility$.next(false);
      }
    }
  }

  ngOnInit(): void {
    combineLatest([this.localeService.locale$, this.visibility$]).pipe(
      switchMap(([, visible]) => {
        if (!visible || !this.selectedPatientID) return EMPTY;
        this.isMoveLoading = true;
        return this.admissionService.getDropdownData();
      }),
      takeUntil(this.destroy$),
    ).subscribe({
      next: (d) => {
        this.moveBeds = (d.data.bedAvailableId || []).flatMap((building) =>
          building.floors.flatMap((floor) =>
            floor.wards.flatMap((ward) =>
              ward.rooms.flatMap((room) =>
                room.beds.map((bed) => ({
                  id: bed.id,
                  name: `${building.name} - ${floor.name} - ${ward.name} - ${room.name} - ${bed.name}`,
                })),
              ),
            ),
          ),
        );
        this.isMoveLoading = false;
      },
      error: () => {
        this.isMoveLoading = false;
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  handleMoveCancel() {
    this.parent.isMoveVisible = false;
    this.visibility$.next(false);

    this.moveForm.reset();

    this.moveSubmitted = false;

    this.isMoveLoading = false;

    this.moveModalRef.destroy();
  }

  moveBedForm() {
    this.moveSubmitted = true;
    this.isMoveLoading = true;

    const moveObj: IAddAccommodation = {
      accommodationDataId: this.moveForm.get('accommodationValue')?.value,
      newBedId: this.moveForm.get('newBedValue')?.value,
      bedNotes: this.moveForm.get('noteValue')?.value,
    };

    this.accommodationService.moveBed(moveObj).subscribe({
      next: (res) => {
        this.swalToastService.toastSuccess('Patient moved successfully');
        this.isMoveLoading = false;
        this.parent.isMoveVisible = false;
        this.visibility$.next(false);

        this.moveForm.reset();

        this.parent.refreshTable();
      },
      error: (err) => {
        this.swalToastService.toastError('Unable to move the Patient');
        this.isMoveLoading = false;
      },
    });
  }
}
